/****************************************************
 * Module Name:  cancelledMixBucketReport.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import { Box, Tooltip, Button } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Constants from "../../utility/constants";
import Utility from "../../utility/utility";
import CommonSnackbar from "../common/commonSnackBar";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import MixSaleProxy from "../../utility/proxy/manager/mixProxy/mixSalesProxy";
import BucketDetailModal from "./bucketDetailModal";

const CancelledMixBucketReport = ({ invoiceDate }) => {

    const [data, setData] = useState([]);
    const [loader, setLoader] = useState();

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");

    const [bucketId, setBucketId] = useState([]);
    const [showBucketDetailModal, setShowBucketDetailModal] = useState(false);

    const [filterValues, setFilterValues] = useState([]);

    const columns = [
        {
            field: "id",
            headerName: "ID",
            headerAlign: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
        },
        {
            field: "invoiceid",
            headerName: "INVOICEID",
            headerAlign: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
        },
        {
            field: "status",
            headerName: "INVOICE DETAIL STATUS",
            headerAlign: "center",
            width: 200,
            valueFormatter: (params) => params.value,
            align: "center",
        },
        {
            field: "bucketid",
            headerName: "BUCKETID",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowBucketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleBucketDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "bucketname",
            headerName: "BUCKET NAME",
            headerAlign: "center",
            align: "center",
            width: 300,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.bucketname : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) =>
                params.row.stonetypefullname +
                " | " +
                params.row.shape +
                " | " +
                params.row.size +
                " | " +
                params.row.color +
                " | " +
                params.row.clarity,
            renderCell: (params) =>
                params.row.stonetypefullname +
                " | " +
                params.row.shape +
                " | " +
                params.row.size +
                " | " +
                params.row.color +
                " | " +
                params.row.clarity,
        },
        {
            field: "sellqty",
            headerName: "SELL QTY",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 80,
        },
        {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            type: "number",
            align: "center",
            width: 70,
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        {
            field: "discount1",
            headerName: "DIS 1",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 55,
        },
        {
            field: "discount2",
            headerName: "DIS 2",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 55,
        },
        {
            field: "discount3",
            headerName: "DIS 3",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 55,
        },
        {
            field: "final_amount",
            headerName: "FINAL AMOUNT",
            headerAlign: "center",
            align: "center",
            width: 110,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.final_amount)
        },
        {
            field: "remarks",
            headerName: "REMARKS",
            headerAlign: "center",
            align: "center",
            width: 90,
        },
    ];

    useEffect(() => {
        handleSearch();
    }, []);

    const handleSearch = () => {
        let data = {
            from: Utility.ConvertLocalTimeToUtc(dayjs(invoiceDate).format('YYYY-MM-DD') + "T00:00:00.000"),
            to: Utility.ConvertLocalTimeToUtc(dayjs(invoiceDate).format('YYYY-MM-DD') + "T23:59:59.999")
        }

        setLoader(true);
        MixSaleProxy.GetCancelledMixBucket(data, (response) => {
            if (response) {
                const bucketNames = [];
                response.forEach(item => {
                    if (item.buckets && item.buckets.length > 0) {
                        item.buckets.forEach(bucket => {
                            const bucketName =
                                `${bucket.stonetypefullname} | ${bucket.shape} | ${bucket.size} | ${bucket.color} | ${bucket.clarity}`;
                            if (!bucketNames.includes(bucketName)) {
                                bucketNames.push(bucketName);
                            }
                        });
                    }
                });

                const combinedFilterValues = {
                    bucketname: bucketNames,
                };
                setData(response.map(data => ({ ...data, invoiceid_bucketid: data.invoiceid + "_" + data.bucketid })));
                setFilterValues(combinedFilterValues);
                setLoader(false);
            } else {
                setLoader(false);
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    }

    const handleBucketDetailModal = (params) => {
        setBucketId(params.value);
        setShowBucketDetailModal(true)
    };

    return (
        <Box sx={{ mt: "8px" }}>

            <CommonSnackbar
                show={showSnackbar}
                message={snackbarMessage}
                variant={snackbarVariant}
                autoHide={Constants.SnackBarCloseTimer}
                title={snackbarVariant === "success" ? "Success" : snackbarVariant === "error" ? "Error" : "Warning"}
                handleClose={() => setShowSnackbar(false)}
            />

            {showBucketDetailModal ? (
                <BucketDetailModal
                    show={showBucketDetailModal}
                    bucketId={bucketId}
                    handleClose={() => setShowBucketDetailModal(false)}
                />
            ) : null}

            {/* Table */}
            <Box
                sx={{
                    marginTop: "8px",
                    backgroundColor: '#fff',
                    color: '#000',
                    width: "100%",
                    position: "relative",
                    "& .MuiTablePagination-selectLabel": {
                        marginTop: "15px",
                    },
                    "& .MuiTablePagination-displayedRows": {
                        marginTop: "15px",
                    },
                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    "& .css-cc8tf1": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    height: '66vh'
                }}
            >
                <DataGridPro
                    disableRowSelectionOnClick={true}
                    localeText={{ noRowsLabel: "No Record Found" }}
                    sx={{
                        '& .MuiDataGrid-columnHeader': {
                            color: '#fff',
                            backgroundColor: 'grey',
                        },
                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                            py: "4px",
                        },
                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                            py: "12px",
                        },
                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                            py: "18px",
                        },

                        fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                    }}
                    density="compact"
                    slots={{ toolbar: GridToolbar }}
                    rows={data}
                    columns={columns}
                    getRowId={(row) => row.invoiceid_bucketid}
                    loading={loader}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                // Hide columns id, the other columns will remain visible
                            },
                        },
                        pagination: {
                            paginationModel: Constants.pageSize,
                        },
                    }}
                    pageSizeOptions={Constants.pageSizeOptions}
                    pagination
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: {
                                debounceMs: 500,
                            },
                        },
                    }}
                />

                <Box style={{ position: "absolute", top: 9, right: "265px", display: "flex", alignItems: "center" }}>
                    {loader ? (
                        <CircularProgress size="1rem" />
                    ) : (
                        <Tooltip title="Reload List">
                            <RefreshOutlinedIcon
                                onClick={() => handleSearch()}
                                color="primary"
                                fontSize="medium"
                                sx={{ cursor: "pointer" }}
                            />
                        </Tooltip>
                    )}
                </Box>

            </Box>

        </Box>
    );
};

export default CancelledMixBucketReport;